// Environmental & Historic Preservation page (Sprint 1.5a — full CRUD).
//
// New: click any permit crossing row → drawer with status workflow,
// permit number entry, and document uploader (issued permit PDF, denial
// letter, etc.) tied to permit_crossings.document_id.

import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import DocumentUploader, { DocumentLink } from '../components/DocumentUploader';

type Project = { id: string; project_name: string; project_code: string };
type Obligation = {
  id: string; code: string; name: string; cadence_rule: string;
  source_cite: string | null; due_date: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'accepted' | 'rejected' | 'overdue' | 'na';
  evidence_url: string | null; notes: string | null;
};
type Crossing = {
  id: string; kind: string; feature_name: string | null;
  feature_owner: string | null; status: string; permit_number: string | null;
  notes: string | null; lng: number; lat: number;
  document_id: string | null;
  detected_at: string; updated_at: string;
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending:     { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted:   { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted:    { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  rejected:    { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue:     { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na:          { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
  needed:      { bg: '#fee2e2', fg: '#991b1b', label: 'Needed' },
  applied:     { bg: '#fef3c7', fg: '#92400e', label: 'Applied' },
  in_review:   { bg: '#dbeafe', fg: '#1e40af', label: 'In review' },
  approved:    { bg: '#d1fae5', fg: '#065f46', label: 'Approved' },
  issued:      { bg: '#d1fae5', fg: '#065f46', label: 'Issued' },
  waived:      { bg: '#e5e7eb', fg: '#6b7280', label: 'Waived' },
  denied:      { bg: '#fecaca', fg: '#7f1d1d', label: 'Denied' },
};

const CROSSING_ICON: Record<string, string> = {
  railroad: '🚆', waterway: '🌊', highway: '🛣', interstate: '🛣',
  bridge: '🌉', other: '⚠',
};

const CROSSING_STATUS_OPTIONS = [
  { v: 'needed',    l: 'Needed (apply)' },
  { v: 'applied',   l: 'Applied' },
  { v: 'in_review', l: 'In review' },
  { v: 'approved',  l: 'Approved' },
  { v: 'issued',    l: 'Issued' },
  { v: 'waived',    l: 'Waived' },
  { v: 'denied',    l: 'Denied' },
];

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '2px 8px',
      borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border, #e2e8f0)',
      borderTop: `3px solid ${color ?? '#10b981'}`,
      borderRadius: 6, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.06em', color: 'var(--text-muted, #64748b)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function EHPPage() {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [editingCrossing, setEditingCrossing] = useState<Crossing | null>(null);

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });
  const activeProjectId = projectId ?? projects?.[0]?.id ?? null;

  const { data: obligations = [] } = useQuery<Obligation[]>({
    queryKey: ['env-obligations', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/obligations?category=federal_environmental`),
    enabled: !!activeProjectId,
  });

  const { data: crossingsData } = useQuery<{ items: Crossing[] }>({
    queryKey: ['permit-crossings', activeProjectId],
    queryFn: () => api.get(`/bead/projects/${activeProjectId}/permit-crossings`),
    enabled: !!activeProjectId,
  });
  const crossings: Crossing[] = crossingsData?.items ?? [];

  const kpis = useMemo(() => {
    const byCode = new Map<string, Obligation>();
    for (const o of obligations) byCode.set(o.code, o);
    const codeStatus = (code: string) => byCode.get(code)?.status ?? 'pending';
    const crossingsOpen = crossings.filter(c =>
      ['needed', 'applied', 'in_review'].includes(c.status)).length;
    return {
      nepa:   codeStatus('FED-ENV-NEPA'),
      s106:   codeStatus('FED-ENV-S106'),
      tribal: codeStatus('FED-ENV-TRIBE'),
      crossingsOpen,
    };
  }, [obligations, crossings]);

  return (
    <div>
      <div className="page__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Environmental & Historic Preservation</h1>
          <div className="page__subtitle" style={{ color: 'var(--text-muted, #64748b)', fontSize: 13 }}>
            NEPA · Section 106 · Tribal consultation · Permits
          </div>
        </div>
        <select
          value={activeProjectId ?? ''}
          onChange={(e) => setProjectId(e.target.value || null)}
          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}
        >
          {(projects ?? []).map(p => (
            <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="NEPA review"        value={STATUS_BADGE[kpis.nepa]?.label ?? '—'}  color={kpis.nepa === 'accepted' ? '#10b981' : '#f59e0b'} />
        <StatCard label="Section 106"        value={STATUS_BADGE[kpis.s106]?.label ?? '—'}  color={kpis.s106 === 'accepted' ? '#10b981' : '#f59e0b'} />
        <StatCard label="Tribal consultation"value={STATUS_BADGE[kpis.tribal]?.label ?? '—'} color={kpis.tribal === 'accepted' ? '#10b981' : '#f59e0b'} />
        <StatCard label="Crossings outstanding" value={kpis.crossingsOpen} sub={`${crossings.length} total detected`} color={kpis.crossingsOpen > 0 ? '#ef4444' : '#10b981'} />
      </div>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Environmental obligations · {obligations.length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Code</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Requirement</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Authority</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Cadence</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Due</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {obligations.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>No environmental obligations on this project.</td></tr>
              ) : obligations.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{o.code}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{o.name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.source_cite ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{(o.cadence_rule ?? '').replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.due_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
            To update an obligation status, attach evidence, or log reviewer notes — head to <b>Compliance → Overview</b> and click any row.
          </div>
        </div>
      </section>

      <section>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Permit crossings · {crossings.length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Kind</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Feature</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Owner</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Permit #</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Doc</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Status</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Detected</th>
              </tr>
            </thead>
            <tbody>
              {crossings.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>
                  No permit crossings detected on this project's fiber path.
                </td></tr>
              ) : crossings.map(c => (
                <tr key={c.id}
                  onClick={() => setEditingCrossing(c)}
                  style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '8px 10px' }}>
                    <span style={{ marginRight: 6 }}>{CROSSING_ICON[c.kind] ?? '⚠'}</span>
                    <span style={{ textTransform: 'capitalize' }}>{(c.kind ?? '').replace(/_/g, ' ')}</span>
                  </td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{c.feature_name ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{c.feature_owner ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11 }}>{c.permit_number ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><DocumentLink documentId={c.document_id} label="permit" /></td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={c.status} /></td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{c.detected_at?.slice(0, 10) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {crossings.length > 0 && (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
            Click any row to update status, attach the issued permit PDF, or log notes.
          </div>
        )}
      </section>

      <SoilCharacterizationSection projectId={activeProjectId} />

            {editingCrossing && activeProjectId && (
        <CrossingEditor
          crossing={editingCrossing}
          projectId={activeProjectId}
          onClose={() => setEditingCrossing(null)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------
// Crossing editor drawer
// --------------------------------------------------------------------

function CrossingEditor({
  crossing, projectId, onClose,
}: { crossing: Crossing; projectId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(crossing.status);
  const [permitNumber, setPermitNumber] = useState(crossing.permit_number ?? '');
  const [notes, setNotes] = useState(crossing.notes ?? '');
  const [docId, setDocId] = useState<string | null>(crossing.document_id);

  const saveMut = useMutation({
    mutationFn: (body: any) => api.put(`/bead/projects/${projectId}/permit-crossings/${crossing.id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['permit-crossings', projectId] });
      onClose();
    },
  });

  // Auto-save when uploader sets a new doc_id (avoids losing the upload
  // if the user closes the drawer without clicking Save).
  useEffect(() => {
    if (docId !== crossing.document_id) {
      saveMut.mutate({ document_id: docId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  function save() {
    saveMut.mutate({
      status, permit_number: permitNumber || null,
      notes: notes || null, document_id: docId,
    });
  }

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 480, maxWidth: '100%', background: 'white', height: '100vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.06em' }}>{crossing.kind}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{crossing.feature_name ?? 'Unnamed crossing'}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Owner: {crossing.feature_owner ?? '—'}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }}>
            {CROSSING_STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Permit number</div>
          <input value={permitNumber} onChange={(e) => setPermitNumber(e.target.value)}
            placeholder="e.g. WYDOT-2026-0418"
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'ui-monospace, monospace' }} />
        </div>

        <div style={{ marginTop: 12 }}>
          <DocumentUploader
            value={docId}
            onChange={setDocId}
            projectId={projectId}
            docType="permit_crossing"
            label="Issued permit PDF / application / denial letter"
            hint="Attached automatically when uploaded."
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>Notes</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Application date, agency contact, special conditions, etc."
            style={{ width: '100%', minHeight: 80, padding: '6px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={save} disabled={saveMut.isPending}
            style={{ padding: '6px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------
// Soil & Wetland Characterization (SSURGO)
// Auto-fills FED-ENV-NEPA soil profile section + flags FED-ENV-USACE /
// FED-ENV-FLOOD risk when project's territory contains hydric soils.
// --------------------------------------------------------------------

type SoilProfileResp = {
  soil_profile: {
    dominant_drainage: string | null;
    dominant_hydro_group: string | null;
    avg_slope_pct: number | null;
    avg_bedrock_depth_cm: number | null;
    hydric_pct_weighted: number | null;
    map_unit_count: number;
    top_soils: Array<{ musym: string; muname: string; area_pct: number }>;
  } | null;
  wetland_risk: {
    hydric_majority_area_m2: number;
    hydric_majority_area_pct: number;
    hydric_polygon_count: number;
    needs_404_review: boolean;
  } | null;
  blended_labor_per_ft: number;
  catalog_default_per_ft: number;
  adjustment_per_ft: number;
  note?: string;
};

function SoilCharacterizationSection({ projectId }: { projectId: string | null }) {
  const { data } = useQuery<SoilProfileResp>({
    queryKey: ['soil-profile-ehp', projectId],
    queryFn: () => api.get(`/geohub-data/soil-profile?bead_project_id=${projectId}`),
    enabled: !!projectId,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  if (!data || !data.soil_profile) {
    return (
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Soil & wetland characterization (SSURGO)</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, padding: 16, color: '#94a3b8', fontSize: 13 }}>
          {data?.note ?? 'No soil data — out of SSURGO coverage area or project boundary missing.'}
        </div>
      </section>
    );
  }

  const sp = data.soil_profile;
  const wr = data.wetland_risk;

  const slope = sp.avg_slope_pct ?? 0;
  const bedrock = sp.avg_bedrock_depth_cm;
  const hydric = sp.hydric_pct_weighted ?? 0;
  const drainage = (sp.dominant_drainage ?? '—').replace(/\s+/g, ' ').trim();

  const flagSlope    = slope > 15 ? '#ef4444' : slope > 8 ? '#f59e0b' : '#10b981';
  const flagBedrock  = bedrock != null && bedrock < 46 ? '#ef4444' : bedrock != null && bedrock < 91 ? '#f59e0b' : '#10b981';
  const flagHydric   = hydric > 5 ? '#ef4444' : hydric > 1 ? '#f59e0b' : '#10b981';
  const flagDrainage = drainage.toLowerCase().startsWith('well') ? '#10b981' : '#f59e0b';

  return (
    <section style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h2 style={{ fontSize: 16, margin: 0 }}>Soil & wetland characterization (SSURGO)</h2>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          {sp.map_unit_count} map units · USDA SSURGO via GeoHub
        </span>
      </div>

      {/* Wetland alert (only when hydric polygons present) */}
      {wr?.needs_404_review && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fbbf24',
          borderRadius: 6, padding: '12px 14px', marginBottom: 12,
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <span style={{ fontSize: 20 }}>⚠</span>
          <div style={{ fontSize: 13, color: '#92400e' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Potential wetland — §404 review may be required</div>
            <div>
              {(wr.hydric_majority_area_pct ?? 0).toFixed(1)}% of the project boundary intersects soils with
              hydric class ≥ 50% ({wr.hydric_polygon_count} map-unit polygons). Triggers
              {' '}<b>FED-ENV-USACE</b> (Clean Water Act §404) and{' '}
              <b>FED-ENV-FLOOD</b> (floodplain/wetland review) obligations.
            </div>
          </div>
        </div>
      )}

      {/* Mini-stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
        <SoilStat label="Dominant drainage" value={drainage} accent={flagDrainage} />
        <SoilStat label="Avg slope"        value={`${slope.toFixed(1)}%`} accent={flagSlope} />
        <SoilStat label="Avg bedrock depth" value={bedrock != null ? `${bedrock.toFixed(0)} cm` : '—'} accent={flagBedrock} />
        <SoilStat label="Hydric (wetland)"  value={`${hydric.toFixed(1)}%`} accent={flagHydric} />
      </div>

      {/* Top soils */}
      <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
            <tr>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Sym</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Soil description</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Area %</th>
            </tr>
          </thead>
          <tbody>
            {(sp.top_soils ?? []).map((t, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{t.musym || '—'}</td>
                <td style={{ padding: '8px 10px' }}>{t.muname}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{(t.area_pct ?? 0).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
          This characterization auto-feeds the <b>FED-ENV-NEPA</b> soil section of the project's Environmental Assessment.
          Construction-method blend: ${(data.blended_labor_per_ft ?? 0).toFixed(2)}/ft (vs catalog default ${(data.catalog_default_per_ft ?? 0).toFixed(2)}/ft).
        </div>
      </div>
    </section>
  );
}

function SoilStat({ label, value, accent }: { label: string; value: string; accent: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border, #e2e8f0)',
      borderTop: `3px solid ${accent}`,
      borderRadius: 6, padding: '10px 12px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.06em', color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}
